import { makeAutoObservable, runInAction } from 'mobx';
import GroupModule from 'store/modules/group/groupModule';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { updateGroup } from 'api/operations/groupService';
import { redirectToRoute } from 'routes/redirectToRoute';
import { RouteV2Id } from 'routes/routesConfigV2';
import { saveProposal as saveProposalToEnrollmentService } from 'api/operations/enrollmentService';
import { createCart } from 'api/operations/cartServiceV2';
import { CartBrowsingCriteria, RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { InitiateQuoteFlowGroupInfoDelegate } from './quoteFlowGroupInfoDelegate';

export interface DomainParams {
  groupId: string;
  nextPageId: RouteV2Id;
}

export interface InitiateQuoteFlowParams {
  domainParams: DomainParams;
}

export interface SaveProposalInput {
  proposalName: string;
  effectiveDate: string;
  rateSegment: RateSegment;
  browsingCriteria: CartBrowsingCriteria;
}

/**
 * Flow delegate for default and sponsor-specific quote initiation. Serves as the starting point for proposal creation
 */
export default class InitiateQuoteFlowDelegate {
  private _domainData: DomainParams;
  private _groupModule: GroupModule;
  private _isInitialDataLoading = false;
  private _cartId = '';
  private _periodId = '';
  public groupInfoDelegate: InitiateQuoteFlowGroupInfoDelegate;

  public get domainData(): DomainParams {
    return this._domainData;
  }

  /** Loading driven by GroupModule (e.g. group fetch). */
  public get isFlowDataLoading(): boolean {
    return this._groupModule.isLoading;
  }

  public get groupModule(): GroupModule {
    return this._groupModule;
  }

  /** Used for the initial page content loader */
  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading || this.isFlowDataLoading;
  }

  public setIsInitialDataLoading(isInitialDataLoading: boolean): void {
    this._isInitialDataLoading = isInitialDataLoading;
  }

  public async getFlowInitialData(): Promise<void> {
    await this._groupModule.getGroup(this._domainData.groupId);
  }

  public async createCartAndSaveProposal({
    effectiveDate,
    proposalName,
    rateSegment,
    browsingCriteria,
  }: SaveProposalInput): Promise<void> {
    const cart = await createCart({
      groupId: this.domainData.groupId,
      name: proposalName,
      rateSegment,
      cartSource: 'AGENT',
      browsingCriteria,
    });

    await runInAction(async () => {
      this._cartId = cart.id;
      const [period] = await Promise.all([
        saveProposalToEnrollmentService(
          {
            groupId: this.domainData.groupId,
            cartName: proposalName,
            cartId: this._cartId,
            effectiveDate,
            productLines: [],
          },
          rateSegment,
        ),
        updateGroup(buildInputGroupFromGroup(this.groupModule.group)),
      ]);

      runInAction(() => {
        this._periodId = period.data?.saveProposal?.id || '';
      });
    });
  }

  public onCompleteRedirect(): void {
    redirectToRoute(this.domainData.nextPageId, { ...this.domainData, cartId: this._cartId, periodId: this._periodId });
  }

  public constructor({ domainParams }: InitiateQuoteFlowParams) {
    this._domainData = domainParams;
    this._groupModule = new GroupModule();
    this.groupInfoDelegate = new InitiateQuoteFlowGroupInfoDelegate(this._groupModule);

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
